<script setup>
// X116 (Grant 2026-10-25, on the assistant's opening "You said you'll use Tap Leagues for: Run competitions. Is that
// right?": "This prompt should clearly show the bits they haven't selected… Perhaps a tickbox list and confirm… With
// the preexisting setup areas ticked"). The goals card IS an editable tickbox list right in the chat: every goal the
// club may tick, in the goals dialog's order, each with its icon, name and one-line meaning; Run competitions first,
// ticked and locked ("Always included"); rows the club already uses start ticked and marked "Already set up" (still
// untickable); unticked rows say "Not picked" so nothing reads as hidden. ONE primary Confirm sends the ticked titles.
// Real checkboxes (keyboard, screen readers), 44px rows, one column at 390. Answered: the rows freeze, no button.
// X131 (Grant 2026-10-26): a ticked row that can be paid for shows "Will you charge…?" (Free / We'll charge) under it, in
// the same item; Confirm waits until every ticked one is answered; payments is not a row, it follows from the answers.
// X183 (Grant 2026-10-27: "too much red on red on red"): calm, and the same look as the home dialog's tiles. A ticked row
// is a white tile with a primary hairline border and a primary left accent; brand red is ONLY the tick, that border and
// the accent. Icons, titles, lines and the status labels (SetupGoalTag, one size) are neutral; the payments line is info.
// Grant 2026-11-02: no per-row Free / We'll charge follow-ups; one last row, "Collect payments through Tap Leagues", says it for
// every ticked row, so Confirm never waits on a hidden answer (the same as the home's goals dialog).
import {computed, ref, watch} from 'vue';
import SetupGoalTag from '~/components/admin/setup/SetupGoalTag.vue';
import {
  COLLECT_PAYMENTS, CONFIRM_LABEL, PAYMENTS_NOTE, canCollect, chargesFromCollect, collectsAtStart, confirmPicks, goalRows,
  rowsPicked,
} from '~/utils/setupGoals';

const props = defineProps({
  question: {type: Object, required: true},
  answered: {type: Boolean, default: false},
  disabled: {type: Boolean, default: false},
});
const emit = defineEmits(['answer']);

const goals = computed(() => goalRows(props.question?.goals));
// The payments row (key 'payments', riding in picked) is shown only when the club can take payments.
const rows = computed(() => (canCollect(goals.value)
  ? [...goals.value, {...COLLECT_PAYMENTS, alreadySetUp: false, chargeable: false}]
  : goals.value));
const startPicks = () => [...rowsPicked(goals.value), ...(canCollect(goals.value) && collectsAtStart(goals.value) ? [COLLECT_PAYMENTS.key] : [])];
const picked = ref(startPicks());
// X121 (R55 M1): a new card (a new question object, not only a new id: Start again's card has the same id) starts from ITS server pre-ticks.
watch(() => props.question, () => { picked.value = startPicks(); });
const paymentsOn = computed(() => picked.value.includes(COLLECT_PAYMENTS.key));
// X124: what the per-goal answers are now (the one payments tick stands for them all).
const charges = computed(() => chargesFromCollect(goals.value, paymentsOn.value));

const isOn = (row) => row.locked || picked.value.includes(row.key);
const setOn = (row, on) => {
  if (row.locked || !row.available || props.answered || props.disabled) return;
  picked.value = on ? [...new Set([...picked.value, row.key])] : picked.value.filter((k) => k !== row.key);
};
const confirm = () => {
  if (props.answered || props.disabled) return;
  emit('answer', {question: props.question, picked: confirmPicks(goals.value, picked.value.filter((k) => k !== COLLECT_PAYMENTS.key), charges.value)});
};
// X124 (R58 M-a): the page reads the on-screen ticks (and, X131, the charge answers) when the admin types a short yes instead of pressing Confirm.
const goalPicks = computed(() => picked.value.filter((k) => k !== COLLECT_PAYMENTS.key));
defineExpose({picked: goalPicks, charges});
const groupId = computed(() => `sgl-${props.question?.id ?? 'goals'}`);
</script>

<template>
  <div class="sgl">
    <div class="sgl-list" role="group" :aria-label="question.text">
      <div
          v-for="r in rows"
          :key="r.key"
          class="sgl-item"
          :class="{'sgl-item--on': isOn(r), 'sgl-item--locked': r.locked, 'sgl-item--frozen': answered}"
      >
        <!-- Grant 2026-11-02: Run competitions is plainly included, never a row that looks untickable: a lock, no box, no border,
             and a line under it before the choices (the home's goals dialog does the same) -->
        <div v-if="r.locked" class="sgl-row sgl-row--locked">
          <span class="sgl-box" aria-hidden="true"><v-icon size="20" icon="mdi-lock-outline"/></span>
          <span class="sgl-ic" aria-hidden="true"><v-icon size="20" :icon="r.icon"/></span>
          <span class="sgl-words">
            <span class="sgl-head">
              <span class="sgl-t">{{ r.title }}</span>
              <setup-goal-tag kind="locked"/>
            </span>
            <span class="sgl-line">{{ r.line }}</span>
          </span>
        </div>
        <label v-else class="sgl-row">
          <!-- A real checkbox: Tab reaches it, Space toggles. -->
          <input
              class="sgl-check"
              type="checkbox"
              :name="groupId + '-' + r.key"
              :checked="isOn(r)"
              :disabled="r.locked || !r.available || answered || disabled"
              :aria-disabled="r.locked ? 'true' : undefined"
              @change="setOn(r, $event.target.checked)"
          >
          <span class="sgl-box" aria-hidden="true">
            <v-icon size="22" :icon="isOn(r) ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline'"/>
          </span>
          <span class="sgl-ic" aria-hidden="true"><v-icon size="20" :icon="r.icon"/></span>
          <span class="sgl-words">
            <span class="sgl-head">
              <span class="sgl-t">{{ r.title }}</span>
              <setup-goal-tag v-if="r.locked" kind="locked"/>
              <setup-goal-tag v-else-if="r.alreadySetUp" kind="done"/>
              <setup-goal-tag v-else-if="!isOn(r)" kind="off"/>
            </span>
            <!-- X119 (R54 L3): the line is part of the label, read once; nothing else points at it -->
            <span class="sgl-line">{{ r.line }}</span>
          </span>
        </label>
      </div>
    </div>
    <p v-if="paymentsOn" class="sgl-note"><v-icon size="16" icon="mdi-information-outline"/>{{ PAYMENTS_NOTE }}</p>
    <div v-if="!answered" class="sgl-confirm">
      <v-btn size="small" color="primary" variant="flat" :disabled="disabled" @click="confirm">{{ CONFIRM_LABEL }}</v-btn>
      <span class="sgl-hint">Tick all that apply.</span>
    </div>
  </div>
</template>

<style scoped lang="scss">
/* One column always: the card sits inside a chat bubble that is 88% of the pane at most, so even at 390 the rows stack. */
.sgl-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 10px;
}

/* One item = one goal: the row (a real checkbox) and, when ticked and chargeable, its Free / We'll charge follow-up. */
.sgl-item {
  border-radius: 12px;
  border: 1.5px solid rgb(var(--v-theme-borderLight));
  background: rgb(var(--v-theme-surface));
  color: rgb(var(--v-theme-text));
  transition: border-color 0.12s ease, background 0.12s ease, box-shadow 0.12s ease;

  &:hover {
    border-color: rgb(var(--v-theme-border));
  }

  &:has(.sgl-check:focus-visible) {
    box-shadow: 0 0 0 3px rgba(var(--v-theme-primary), 0.35);
  }

  /* Ticked: white stays white; the primary border and the primary tick say so (Grant 2026-11-02: no left accent). */
  &--on {
    border-color: rgb(var(--v-theme-primary));

    .sgl-box {
      color: rgb(var(--v-theme-primary));
    }
  }

  &--locked,
  &--frozen {
    .sgl-row {
      cursor: default;
    }

    &:hover {
      border-color: rgb(var(--v-theme-borderLight));
    }
  }

  &--on.sgl-item--frozen:hover {
    border-color: rgb(var(--v-theme-primary));
  }

  /* Run competitions: no tile, a quiet row with a lock, and a line under it before the choices. */
  &--locked,
  &--locked:hover {
    border: 0;
    border-radius: 0;
    background: transparent;
    box-shadow: none;
    padding-bottom: 6px;
    margin-bottom: 4px;
    border-bottom: 1px solid rgb(var(--v-theme-borderLight));

    .sgl-box {
      color: rgb(var(--v-theme-textSubtle));
    }
  }
}

.sgl-row--locked {
  cursor: default;
  padding-left: 11.5px;
  padding-right: 11.5px;
}

/* The row itself: the input is visually hidden but keeps focus and the keyboard; the row shows the state. */
.sgl-row {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  min-height: 44px;
  padding: 8px 10px;
  cursor: pointer;
}

/* X201 (R81 L7): no phone layout of its own any more: at 390 the row keeps tick + icon + words on one line, exactly as the
   home's goals dialog does (X132's stacked words made the two surfaces differ). */

.sgl-check {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  opacity: 0;
  cursor: inherit;
}

/* The visible box: the state every viewer reads at a glance (ticked vs an empty box), never hidden. */
.sgl-box {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  margin-top: 3px;
  color: rgb(var(--v-theme-charcoal));
}

.sgl-ic {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 9px;
  background: rgb(var(--v-theme-light));
  color: rgb(var(--v-theme-charcoal));
}

.sgl-words {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
  flex: 1 1 auto;
}

.sgl-head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 8px;
}

.sgl-t {
  font-size: 14px;
  font-weight: 700;
  line-height: 1.35;
  word-break: break-word;
  color: rgb(var(--v-theme-text));
}

.sgl-line {
  font-size: 12.5px;
  line-height: 1.45;
  color: rgb(var(--v-theme-charcoal));
}

.sgl-note {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 10px 0 0;
  font-size: 12.5px;
  color: rgb(var(--v-theme-charcoal));

  .v-icon {
    color: rgb(var(--v-theme-info));
  }
}

.sgl-confirm {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 10px;
}

.sgl-hint {
  font-size: 12px;
  color: rgb(var(--v-theme-textSubtle));
}
</style>
